import { useState } from 'react'
import axios from 'axios'
import { ImpresCatalogItem } from './impresTypes'
import { useCancellableRequest } from '../useCancellableRequest'

const DEFAULT_ERROR_MESSAGE = 'Failed to search the IMPRES catalog. Please try again.'

// Returns a flat list of matches, not keyed by search term like the other catalogs
// a free-text search can match many products, not just one.
export function useImpresCatalogSearch() {
  const [requestedTerm, setRequestedTerm] = useState('')
  const [results, setResults] = useState<ImpresCatalogItem[] | null>(null)
  const [loading, setLoading] = useState<'' | 'loading' | 'error'>('')
  const [errorMessage, setErrorMessage] = useState(DEFAULT_ERROR_MESSAGE)
  const { start, cancel: cancelRequest } = useCancellableRequest()

  async function search(term: string) {
    const trimmed = term.trim()
    const { signal, isCurrent } = start()
    setLoading('loading')
    try {
      const res = await axios.post<ImpresCatalogItem[]>('/impres/catalog-search', { term: trimmed }, { signal })
      if (!isCurrent()) return
      setRequestedTerm(trimmed)
      setResults(res.data)
      setLoading('')
    } catch (err) {
      if (!isCurrent()) return
      if (axios.isCancel(err)) return
      console.error('Failed to search IMPRES catalog', err)
      const backendMessage = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message
        : null
      setErrorMessage(backendMessage || DEFAULT_ERROR_MESSAGE)
      setLoading('error')
    }
  }

  function cancel() {
    cancelRequest()
    setLoading('')
  }

  function dismissError() {
    setLoading('')
  }

  return { requestedTerm, results, loading, errorMessage, search, cancel, dismissError }
}
